/**
 * Carrom Game — Main Container Component
 *
 * Orchestrates: mode selection → engine init → aiming → shot execution →
 * physics simulation → turn management → game over.
 *
 * Wrapped in ErrorBoundary. Uses useAuthentication().
 * First Canvas-based game on the platform.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationType } from '../../services/NotificationService';
import { GameSessionManager } from '../../services/GameSessionManager';
import { CarromEngine } from '../CarromEngine';
import { CarromAI } from '../CarromAI';
import { CarromCanvas } from './CarromCanvas';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import { UniversalGameLayout, UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';
import { CarromErrorHandler } from '../services/CarromErrorHandler';
import { CarromStatisticsService } from '../services/CarromStatisticsService';
import { CarromGamePersistence } from '../services/CarromGamePersistence';
import { MultiplayerCarromService } from '../services/MultiplayerCarromService';
import { ErrorHandlingService } from '../../services/ErrorHandlingService';
import { NotificationService } from '../../services/NotificationService';
import { StatisticsManager } from '../../services/StatisticsManager';
import { ProfileManager } from '../../services/ProfileManager';
import { LocalStorageManager } from '../../services/LocalStorageManager';
import {
  CarromGameSettings,
  CarromGameState,
  CarromPlayer,
  ShotInput,
  ShotResult,
  CarromGameProps,
  PlayerType,
  BoardSettings,
  PowderType,
  SpeedPreset,
  CoinLayout,
  POWDER_CONFIG,
  SPEED_CONFIG,
  COIN_LAYOUT_CONFIG,
} from '../types';
import './CarromGame.css';

type GameView = 'mode-select' | 'playing' | 'game-over';
type GamePhase = 'waiting' | 'aiming' | 'simulating' | 'processing' | 'completed';

const CarromGameInner: React.FC<CarromGameProps> = ({
  onExit,
  initialRoomCode,
}) => {
  const { isAuthenticated, user } = useAuthentication();
  const { addNotification } = useNotifications();

  // Game state
  const [view, setView] = useState<GameView>('mode-select');
  const [settings, setSettings] = useState<CarromGameSettings | null>(null);
  const [gameState, setGameState] = useState<CarromGameState | null>(null);
  const [gamePhase, setGamePhase] = useState<GamePhase>('waiting');
  const [lastResult, setLastResult] = useState<ShotResult | null>(null);
  const [boardSettings, setBoardSettings] = useState<BoardSettings>({ powder: 'light', speed: 'normal', coinLayout: 'standard' });
  const [showBoardSettings, setShowBoardSettings] = useState(false);
  const [powderAnimating, setPowderAnimating] = useState(false);

  // Refs
  const engineRef = useRef<CarromEngine>(new CarromEngine());
  const aiRef = useRef<CarromAI | null>(null);
  const gameSessionIdRef = useRef<string | null>(null);
  const gameSessionManager = useRef(GameSessionManager.getInstance());
  const simulationCheckRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentShotRef = useRef<ShotInput | null>(null);

  // Services (lazy init)
  const errorHandlerRef = useRef<CarromErrorHandler | null>(null);
  const statsServiceRef = useRef<CarromStatisticsService | null>(null);
  const persistenceRef = useRef<CarromGamePersistence | null>(null);
  const multiplayerRef = useRef<MultiplayerCarromService | null>(null);

  // Initialize services once
  useEffect(() => {
    try {
      const errorService = ErrorHandlingService.getInstance();
      const notifService = NotificationService.getInstance();
      errorHandlerRef.current = new CarromErrorHandler(errorService, notifService);

      const profileManager = new ProfileManager();
      const statsManager = new StatisticsManager(profileManager);
      statsServiceRef.current = new CarromStatisticsService(statsManager);

      const storageManager = new LocalStorageManager();
      persistenceRef.current = new CarromGamePersistence(storageManager);
    } catch (e) {
      console.error('Failed to initialize Carrom services:', e);
    }

    return () => {
      aiRef.current?.destroy();
      if (simulationCheckRef.current) {
        clearInterval(simulationCheckRef.current);
      }
    };
  }, []);

  // =========================================================================
  // Game Flow
  // =========================================================================

  const handleStartGame = useCallback(
    (newSettings: CarromGameSettings) => {
      const engine = engineRef.current;

      const playerTypes: Record<CarromPlayer, PlayerType> = {
        player1: 'human',
        player2: newSettings.mode === 'singleplayer' ? 'ai' : 'human',
      };

      engine.initializeGame(playerTypes);

      setSettings(newSettings);
      setGameState(engine.getGameState());
      setGamePhase('waiting');
      setLastResult(null);
      setView('playing');

      // Track game session
      gameSessionIdRef.current = gameSessionManager.current.startGameSession('carrom');

      // Set up AI if single player
      if (newSettings.mode === 'singleplayer' && newSettings.aiDifficulty) {
        aiRef.current = new CarromAI(newSettings.aiDifficulty);
      } else {
        aiRef.current?.destroy();
        aiRef.current = null;
      }

      addNotification({
        type: NotificationType.SUCCESS,
        title: 'Game Started',
        message: `Carrom ${newSettings.mode === 'singleplayer' ? 'single player' : 'multiplayer'} game started!`,
        duration: 2000,
      });
    },
    [addNotification]
  );

  // Use a ref to track game phase without stale closures
  const gamePhaseRef = useRef<GamePhase>('waiting');
  const settingsRef = useRef<CarromGameSettings | null>(null);

  // Keep refs in sync
  useEffect(() => { gamePhaseRef.current = gamePhase; }, [gamePhase]);
  useEffect(() => { settingsRef.current = settings; }, [settings]);

  const doHandleShot = useCallback(
    (shot: ShotInput) => {
      const engine = engineRef.current;
      if (engine.isGameOver() || gamePhaseRef.current === 'simulating') return;

      currentShotRef.current = shot;
      setGamePhase('simulating');
      gamePhaseRef.current = 'simulating';

      // Execute the shot in the engine (sets up physics)
      engine.executeShot(shot);

      // Start checking for simulation completion
      simulationCheckRef.current = setInterval(() => {
        const physics = engine.getPhysics();
        if (physics.areAllStopped()) {
          if (simulationCheckRef.current) {
            clearInterval(simulationCheckRef.current);
            simulationCheckRef.current = null;
          }
          // Process inline to avoid stale closure issues
          doHandleSimulationComplete();
        }
      }, 100);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Store handleShot in a ref so AI callback always gets the latest
  const handleShotRef = useRef(doHandleShot);
  useEffect(() => { handleShotRef.current = doHandleShot; }, [doHandleShot]);

  // Wrapper that delegates to the ref (stable identity for props)
  const handleShot = useCallback((shot: ShotInput) => {
    handleShotRef.current(shot);
  }, []);

  const doHandleSimulationComplete = useCallback(() => {
    const engine = engineRef.current;
    const shot = currentShotRef.current;
    if (!shot) return;

    setGamePhase('processing');
    gamePhaseRef.current = 'processing';

    // Process results
    const result = engine.processResults(shot);
    setLastResult(result);

    const state = engine.getGameState();
    setGameState(state);

    // Show notifications
    if (result.pocketedCoins.length > 0) {
      const coinTypes = result.pocketedCoins.map((c) => c.type).join(', ');
      addNotification({
        type: NotificationType.SUCCESS,
        title: '🎯 Pocketed!',
        message: `Pocketed: ${coinTypes}`,
        duration: 2000,
      });
    }

    if (result.foul) {
      addNotification({
        type: NotificationType.WARNING,
        title: '⚠️ Foul!',
        message: result.foulReason || 'Foul committed',
        duration: 3000,
      });
    }

    if (result.queenCovered) {
      addNotification({
        type: NotificationType.SUCCESS,
        title: '👑 Queen Covered!',
        message: 'Queen has been covered! +3 bonus points',
        duration: 3000,
      });
    }

    // Auto-save
    const curSettings = settingsRef.current;
    if (persistenceRef.current && curSettings) {
      persistenceRef.current.autoSave(curSettings.mode, state, {
        aiDifficulty: curSettings.aiDifficulty,
        startedAt: state.startedAt,
        lastSavedAt: Date.now(),
      });
    }

    // Check game over
    if (state.gameStatus.type !== 'active') {
      doHandleGameOver(state);
      return;
    }

    // Short delay then set up next turn
    setTimeout(() => {
      setGamePhase('waiting');
      gamePhaseRef.current = 'waiting';
      currentShotRef.current = null;

      // Schedule AI turn ONLY if it's actually the AI's turn
      const freshState = engine.getGameState();
      const nextPlayer = freshState.currentTurn;
      const nextPlayerType = freshState.playerTypes[nextPlayer];
      if (nextPlayerType === 'ai' && aiRef.current && freshState.gameStatus.type === 'active') {
        aiRef.current.scheduleShot(
          engineRef.current,
          nextPlayer,
          (aiShot) => {
            handleShotRef.current(aiShot);
          }
        );
      }
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addNotification]);

  const doHandleGameOver = useCallback(
    (state: CarromGameState) => {
      setGamePhase('completed');
      gamePhaseRef.current = 'completed';
      setView('game-over');

      // End game session
      if (gameSessionIdRef.current) {
        gameSessionManager.current.endGameSession(gameSessionIdRef.current, true);
        gameSessionIdRef.current = null;
      }

      // Clear auto-save
      persistenceRef.current?.clearAutoSave();

      // Record statistics
      const curSettings = settingsRef.current;
      if (statsServiceRef.current && curSettings) {
        const winner = state.gameStatus.type === 'win' ? state.gameStatus.winner : undefined;
        statsServiceRef.current.recordGameResult(
          {
            gameId: state.gameId,
            mode: curSettings.mode,
            difficulty: curSettings.aiDifficulty,
            winner,
            reason: 'all_coins_pocketed',
            player1Score: state.player1Score,
            player2Score: state.player2Score,
            totalShots: state.moveHistory.length,
            duration: Math.floor((Date.now() - state.startedAt) / 1000),
            completedAt: Date.now(),
          },
          curSettings.mode === 'singleplayer' ? 'player1' : (winner || 'player1'),
          isAuthenticated ? user?.id : undefined
        );
      }

      const statusType = state.gameStatus.type;
      if (statusType === 'win') {
        const winner = state.gameStatus.winner;
        addNotification({
          type: NotificationType.SUCCESS,
          title: '🎉 Game Over!',
          message: `${winner === 'player1' ? 'Player 1 (White)' : 'Player 2 (Black)'} wins!`,
          duration: 5000,
        });
      } else {
        addNotification({
          type: NotificationType.INFO,
          title: '🤝 Draw!',
          message: 'The game ended in a draw!',
          duration: 5000,
        });
      }
    },
    [addNotification, isAuthenticated, user]
  );

  const handleNewGame = useCallback(() => {
    aiRef.current?.destroy();
    aiRef.current = null;
    if (simulationCheckRef.current) {
      clearInterval(simulationCheckRef.current);
      simulationCheckRef.current = null;
    }
    setView('mode-select');
    setGameState(null);
    setLastResult(null);
    setSettings(null);
    setGamePhase('waiting');
    currentShotRef.current = null;
  }, []);

  const handleExit = useCallback(() => {
    if (gameSessionIdRef.current) {
      gameSessionManager.current.endGameSession(gameSessionIdRef.current, false);
      gameSessionIdRef.current = null;
    }
    aiRef.current?.destroy();
    if (simulationCheckRef.current) {
      clearInterval(simulationCheckRef.current);
    }
    onExit?.();
  }, [onExit]);

  const handlePowderChange = useCallback((powder: PowderType) => {
    const newFriction = POWDER_CONFIG[powder].friction;
    engineRef.current.getPhysics().updateConfig({ friction: newFriction });
    const newSettings = { ...boardSettings, powder };
    setBoardSettings(newSettings);
    // Trigger powder animation
    setPowderAnimating(true);
    setTimeout(() => setPowderAnimating(false), 2000);
    // Broadcast to opponent in multiplayer
    if (multiplayerRef.current) {
      multiplayerRef.current.sendBoardSettings(newSettings);
    }
    addNotification({
      type: NotificationType.INFO,
      title: `${POWDER_CONFIG[powder].emoji} ${POWDER_CONFIG[powder].label}`,
      message: POWDER_CONFIG[powder].description,
      duration: 2000,
    });
  }, [addNotification, boardSettings]);

  const handleSpeedChange = useCallback((speed: SpeedPreset) => {
    const newMaxSpeed = SPEED_CONFIG[speed].maxSpeed;
    engineRef.current.getPhysics().updateConfig({ maxSpeed: newMaxSpeed });
    const newSettings = { ...boardSettings, speed };
    setBoardSettings(newSettings);
    // Broadcast to opponent in multiplayer
    if (multiplayerRef.current) {
      multiplayerRef.current.sendBoardSettings(newSettings);
    }
    addNotification({
      type: NotificationType.INFO,
      title: `🎚️ Speed: ${SPEED_CONFIG[speed].label}`,
      message: `Max shot speed set to ${SPEED_CONFIG[speed].label.toLowerCase()}`,
      duration: 1500,
    });
  }, [addNotification, boardSettings]);

  /** Apply board settings received from the other player (multiplayer sync) */
  const applyBoardSettings = useCallback((incoming: BoardSettings) => {
    const newFriction = POWDER_CONFIG[incoming.powder].friction;
    const newMaxSpeed = SPEED_CONFIG[incoming.speed].maxSpeed;
    engineRef.current.getPhysics().updateConfig({ friction: newFriction, maxSpeed: newMaxSpeed });
    if (incoming.coinLayout !== boardSettings.coinLayout) {
      engineRef.current.resetCoinLayout(incoming.coinLayout);
      setGameState(engineRef.current.getGameState());
    }
    setBoardSettings(incoming);
    if (incoming.powder !== boardSettings.powder) {
      setPowderAnimating(true);
      setTimeout(() => setPowderAnimating(false), 2000);
    }
    addNotification({
      type: NotificationType.INFO,
      title: '⚙️ Settings Updated',
      message: `Opponent changed: ${POWDER_CONFIG[incoming.powder].label}, ${SPEED_CONFIG[incoming.speed].label} speed`,
      duration: 2500,
    });
  }, [addNotification, boardSettings]);

  const handleCoinLayoutChange = useCallback((layout: CoinLayout) => {
    engineRef.current.resetCoinLayout(layout);
    setGameState(engineRef.current.getGameState());
    const newSettings = { ...boardSettings, coinLayout: layout };
    setBoardSettings(newSettings);
    if (multiplayerRef.current) {
      multiplayerRef.current.sendBoardSettings(newSettings);
    }
    addNotification({
      type: NotificationType.INFO,
      title: `${COIN_LAYOUT_CONFIG[layout].emoji} ${COIN_LAYOUT_CONFIG[layout].label} Layout`,
      message: COIN_LAYOUT_CONFIG[layout].description,
      duration: 2000,
    });
  }, [addNotification, boardSettings]);

  // Trigger AI on initial turn if AI goes first
  useEffect(() => {
    if (view !== 'playing' || !gameState || !aiRef.current) return;
    if (gamePhaseRef.current !== 'waiting') return;
    const state = engineRef.current.getGameState();
    const currentPlayer = state.currentTurn;
    const playerType = state.playerTypes[currentPlayer];
    if (playerType === 'ai' && !engineRef.current.isGameOver()) {
      aiRef.current.scheduleShot(
        engineRef.current,
        currentPlayer,
        (shot) => {
          handleShotRef.current(shot);
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // =========================================================================
  // Render
  // =========================================================================

  if (view === 'mode-select') {
    const carromConfig: GameModeConfig = {
      gameName: 'Carrom',
      gameIcon: '🎯',
      subtitle: 'Tabletop flicking game',
      availableModes: ['ai', 'local', 'online'],
    };

    const handleModeSelection = (selection: GameModeSelection) => {
      if (selection.mode === 'ai') {
        handleStartGame({
          mode: 'singleplayer',
          aiDifficulty: (selection.difficulty as any) || 'medium',
        });
      } else if (selection.mode === 'local') {
        handleStartGame({
          mode: 'local-multiplayer',
        });
      } else if (selection.mode === 'online') {
        handleStartGame({ mode: 'multiplayer-realtime' });
      }
    };

    return (
      <div className="carrom-game">
        <UniversalGameModeSelector
          config={carromConfig}
          onStart={handleModeSelection}
          isAuthenticated={isAuthenticated}
          guestGamesRemaining={isAuthenticated ? undefined : 3}
        />
      </div>
    );
  }

  if (!gameState) return null;

  const currentPlayer = gameState.currentTurn;
  const currentPlayerType = gameState.playerTypes[currentPlayer];
  const isHumanTurn = currentPlayerType === 'human';
  const isSimulating = gamePhase === 'simulating';

  // Game over config
  const gameOverConfig = (view === 'game-over') ? {
    show: true,
    result: (gameState.gameStatus.type === 'win' ? 'win' : 'draw') as 'win' | 'loss' | 'draw',
    title: gameState.gameStatus.type === 'win' ? '🎉 Game Over!' : '🤝 Draw!',
    subtitle: gameState.gameStatus.type === 'win'
      ? `${gameState.gameStatus.winner === 'player1' ? 'Player 1 (White)' : 'Player 2 (Black)'} wins! (P1: ${gameState.player1Score}, P2: ${gameState.player2Score}, ${gameState.moveHistory.length} shots)`
      : `Draw! (P1: ${gameState.player1Score}, P2: ${gameState.player2Score})`,
    icon: gameState.gameStatus.type === 'win' ? '🎉' : '🤝',
    onPlayAgain: handleNewGame,
    onExit: handleExit,
  } : undefined;

  const layoutConfig: UniversalGameLayoutConfig = {
    gameName: 'Carrom',
    board: (
      <div className="carrom-game__board-area">
        <CarromCanvas
          physics={engineRef.current.getPhysics()}
          coins={gameState.coins}
          currentPlayer={currentPlayer}
          isSimulating={isSimulating}
          isPlayerTurn={isHumanTurn && gamePhase === 'waiting'}
          onShot={handleShot}
          gameOver={gameState.gameStatus.type !== 'active'}
          powderType={boardSettings.powder}
          powderAnimating={powderAnimating}
        />
      </div>
    ),
    leftPanels: [
      {
        id: 'scores',
        title: 'Scores',
        icon: '📊',
        content: (
          <div className="carrom-game__scores">
            <div className={`carrom-game__score-row ${currentPlayer === 'player1' ? 'carrom-game__score-row--active' : ''}`}>
              <span className="carrom-game__player-dot carrom-game__player-dot--player1" aria-hidden="true" />
              <span className="carrom-game__player-name">
                {gameState.playerTypes.player1 === 'ai' ? 'AI' : 'Player 1'} ({gameState.player1Assignment})
              </span>
              <span className="carrom-game__score-value">{gameState.player1Score}</span>
            </div>
            <div className={`carrom-game__score-row ${currentPlayer === 'player2' ? 'carrom-game__score-row--active' : ''}`}>
              <span className="carrom-game__player-dot carrom-game__player-dot--player2" aria-hidden="true" />
              <span className="carrom-game__player-name">
                {gameState.playerTypes.player2 === 'ai' ? 'AI' : 'Player 2'} ({gameState.player2Assignment})
              </span>
              <span className="carrom-game__score-value">{gameState.player2Score}</span>
            </div>
          </div>
        ),
      },
      {
        id: 'coins',
        title: 'Coins',
        icon: '⚪',
        content: (
          <div className="carrom-game__coin-counts">
            <div className="carrom-game__coin-count">
              <span className="carrom-game__coin-dot carrom-game__coin-dot--white" aria-hidden="true" />
              <span>{gameState.coins.filter((c) => c.type === 'white' && !c.pocketed).length} remaining</span>
            </div>
            <div className="carrom-game__coin-count">
              <span className="carrom-game__coin-dot carrom-game__coin-dot--black" aria-hidden="true" />
              <span>{gameState.coins.filter((c) => c.type === 'black' && !c.pocketed).length} remaining</span>
            </div>
            <div className="carrom-game__coin-count">
              <span className="carrom-game__coin-dot carrom-game__coin-dot--queen" aria-hidden="true" />
              <span>Queen: {gameState.queenCovered ? 'Covered ✓' : gameState.queenPocketed ? 'Pocketed (needs cover)' : 'On board'}</span>
            </div>
            {gameState.pendingQueenCover && (
              <div className="carrom-game__queen-warning" role="alert">
                👑 Queen needs to be covered! Pocket one of your coins this turn.
              </div>
            )}
          </div>
        ),
      },
      {
        id: 'board-settings',
        title: 'Board Settings',
        icon: '⚙️',
        defaultFolded: true,
        content: (
          <div className="carrom-game__board-settings" role="group" aria-label="Board settings">
            {/* Powder Selection */}
            <div className="carrom-game__setting-group">
              <label className="carrom-game__setting-label">Powder Type</label>
              <div className="carrom-game__powder-options">
                {(Object.keys(POWDER_CONFIG) as PowderType[]).map((powder) => (
                  <button
                    key={powder}
                    className={`carrom-game__powder-btn ${boardSettings.powder === powder ? 'carrom-game__powder-btn--active' : ''}`}
                    onClick={() => handlePowderChange(powder)}
                    title={POWDER_CONFIG[powder].description}
                    aria-pressed={boardSettings.powder === powder}
                  >
                    <span className="carrom-game__powder-emoji">{POWDER_CONFIG[powder].emoji}</span>
                    <span className="carrom-game__powder-name">{POWDER_CONFIG[powder].label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Speed Control */}
            <div className="carrom-game__setting-group">
              <label className="carrom-game__setting-label">Shot Speed</label>
              <div className="carrom-game__speed-options">
                {(Object.keys(SPEED_CONFIG) as SpeedPreset[]).map((speed) => (
                  <button
                    key={speed}
                    className={`carrom-game__speed-btn ${boardSettings.speed === speed ? 'carrom-game__speed-btn--active' : ''}`}
                    onClick={() => handleSpeedChange(speed)}
                    aria-pressed={boardSettings.speed === speed}
                  >
                    {SPEED_CONFIG[speed].label}
                  </button>
                ))}
              </div>
            </div>

            {/* Coin Layout */}
            <div className="carrom-game__setting-group">
              <label className="carrom-game__setting-label">Coin Layout</label>
              <div className="carrom-game__layout-options">
                {(Object.keys(COIN_LAYOUT_CONFIG) as CoinLayout[]).map((layout) => (
                  <button
                    key={layout}
                    className={`carrom-game__layout-btn ${boardSettings.coinLayout === layout ? 'carrom-game__layout-btn--active' : ''}`}
                    onClick={() => handleCoinLayoutChange(layout)}
                    title={COIN_LAYOUT_CONFIG[layout].description}
                    aria-pressed={boardSettings.coinLayout === layout}
                  >
                    <span>{COIN_LAYOUT_CONFIG[layout].emoji}</span>
                    <span>{COIN_LAYOUT_CONFIG[layout].label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 'last-shot',
        title: 'Last Shot',
        icon: '🎯',
        content: (
          <div className="carrom-game__last-result">
            {lastResult ? (
              <>
                {lastResult.pocketedCoins.length > 0 ? (
                  <p>Pocketed: {lastResult.pocketedCoins.map((c) => c.type).join(', ')}</p>
                ) : (
                  <p>No coins pocketed</p>
                )}
                {lastResult.foul && <p className="carrom-game__foul">Foul: {lastResult.foulReason}</p>}
                {lastResult.continueTurn && <p className="carrom-game__extra">Continue turn!</p>}
              </>
            ) : (
              <p>No shots yet</p>
            )}
            <div className="carrom-game__move-count">
              Shots played: {gameState.moveHistory.length}
            </div>
          </div>
        ),
      },
    ],
    statusBar: {
      turnIndicator: (
        <span>
          <span
            className={`carrom-game__player-dot carrom-game__player-dot--${currentPlayer}`}
            aria-hidden="true"
          />
          {' '}
          {currentPlayerType === 'ai'
            ? 'AI is thinking...'
            : `${currentPlayer === 'player1' ? 'Player 1' : 'Player 2'}'s turn`}
          {isSimulating && (
            <span className="carrom-game__phase-hint"> — Shot in progress...</span>
          )}
        </span>
      ),
      gameStatus: gameState.gameStatus.type !== 'active' ? 'Game Over' : undefined,
      gameStatusType: gameState.gameStatus.type !== 'active' ? 'ended' : 'active',
      aiThinking: currentPlayerType === 'ai',
    },
    actions: [
      { id: 'new-game', label: 'New Game', icon: '🔄', onClick: handleNewGame, variant: 'primary' },
      { id: 'exit', label: 'Exit', icon: '🚪', onClick: handleExit, variant: 'secondary' },
    ],
    gameOver: gameOverConfig,
  };

  return (
    <div className="carrom-game">
      <UniversalGameLayout config={layoutConfig} />
    </div>
  );
};

/** Wrapped in ErrorBoundary */
const CarromGame: React.FC<CarromGameProps> = (props) => (
  <ErrorBoundary componentName="Carrom">
    <CarromGameInner {...props} />
  </ErrorBoundary>
);

export { CarromGame };
export default CarromGame;
